import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@yhelp/ui';
import type { Ticket } from '@yhelp/shared-types';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { useMe } from '@/hooks/useMe';
import { TourSpotlight, tooltipPositionForTarget } from './TourSpotlight';
import { WelcomeModal } from './WelcomeModal';
import {
  getTourById,
  shouldShowWelcome,
  TOUR_CATALOG,
  TOUR_VERSION,
  toursForRole,
  type OnboardingPrefs,
  type TourDefinition,
} from './tour-definitions';

export { TOUR_VERSION, TOUR_CATALOG, toursForRole, getTourById };

interface TourContextValue {
  startTour: (tourId: string) => void;
  isActive: boolean;
  activeTourId: string | null;
}

const TourContext = createContext<TourContextValue | null>(null);

export function ProductTourProvider({ children }: { children: ReactNode }) {
  const { data: me, refetch } = useMe();
  const authorizedFetch = useAuthorizedFetch();
  const navigate = useNavigate();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({
    top: 0,
    left: 0,
    transform: 'translate(-50%, -50%)',
  });

  const preferences = (me?.data.user as { preferences?: { onboarding?: OnboardingPrefs } })
    ?.preferences;
  const onboarding = preferences?.onboarding;

  const tour: TourDefinition | undefined = activeTourId ? getTourById(activeTourId) : undefined;
  const steps = tour?.steps ?? [];
  const step = steps[index];
  const active = Boolean(activeTourId && step);

  const needsTicket = Boolean(step?.openFirstTicket);
  const { data: ticketsData } = useQuery({
    queryKey: ['tickets', 'tour'],
    queryFn: () => authorizedFetch<{ data: Ticket[] }>('/v1/tickets'),
    enabled: active && needsTicket,
  });
  const firstTicketId = ticketsData?.data[0]?.id;

  useEffect(() => {
    if (!me?.data) return;
    if (shouldShowWelcome(onboarding)) {
      setWelcomeOpen(true);
    }
  }, [me?.data, onboarding]);

  const patchOnboarding = useCallback(
    async (patch: Partial<OnboardingPrefs>) => {
      const current = onboarding ?? {};
      await authorizedFetch('/v1/me', {
        method: 'PATCH',
        body: JSON.stringify({
          preferences: {
            onboarding: {
              ...current,
              tourVersion: TOUR_VERSION,
              ...patch,
              tours: {
                ...(current.tours ?? {}),
                ...(patch.tours ?? {}),
              },
            },
          },
        }),
      });
      void refetch();
    },
    [authorizedFetch, onboarding, refetch],
  );

  const finishTour = useCallback(
    async (skipped: boolean) => {
      if (!activeTourId) return;
      setActiveTourId(null);
      setIndex(0);
      await patchOnboarding({
        tours: {
          [activeTourId]: {
            completedAt: skipped ? null : new Date().toISOString(),
            skipped,
          },
        },
      });
    },
    [activeTourId, patchOnboarding],
  );

  const startTour = useCallback(
    (tourId: string) => {
      setWelcomeOpen(false);
      setActiveTourId(tourId);
      setIndex(0);
      const def = getTourById(tourId);
      if (def?.steps[0]?.route) {
        void navigate({ to: def.steps[0].route });
      }
    },
    [navigate],
  );

  const dismissWelcome = useCallback(async () => {
    setWelcomeOpen(false);
    await patchOnboarding({
      welcomeSeenAt: new Date().toISOString(),
      welcomeDismissed: true,
    });
  }, [patchOnboarding]);

  const startWelcomeTour = useCallback(async () => {
    await patchOnboarding({ welcomeSeenAt: new Date().toISOString(), welcomeDismissed: false });
    startTour('welcome');
  }, [patchOnboarding, startTour]);

  useEffect(() => {
    if (!active || !step) return;

    if (step.route) {
      void navigate({ to: step.route });
    }

    if (step.openFirstTicket && firstTicketId) {
      void navigate({ to: '/tickets/$ticketId', params: { ticketId: firstTicketId } });
    }
  }, [active, step, navigate, firstTicketId]);

  useEffect(() => {
    if (!active || !step) return;

    const updatePos = () => {
      setTooltipPos(tooltipPositionForTarget(step.target, step.placement ?? 'bottom'));
    };

    updatePos();
    const timer = window.setTimeout(updatePos, 450);
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [active, step, index]);

  const skipStep = step?.openFirstTicket && !firstTicketId && step.target !== 'ticket-row-first';

  const contextValue = useMemo(
    () => ({ startTour, isActive: active, activeTourId }),
    [startTour, active, activeTourId],
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {welcomeOpen ? (
        <WelcomeModal
          onStartTour={() => void startWelcomeTour()}
          onDismiss={() => void dismissWelcome()}
        />
      ) : null}
      {active && step && tour ? (
        <div className="fixed inset-0 z-[90]" role="presentation">
          <TourSpotlight target={step.target} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-title"
            aria-live="polite"
            className="fixed z-[91] w-[min(24rem,calc(100%-2rem))] max-h-[calc(100vh-2rem)] overflow-y-auto animate-fade-in-up rounded-2xl border-2 border-yhelp-yellow bg-white p-5 shadow-brand motion-reduce:animate-none dark:border-yhelp-yellow dark:bg-neutral-900"
            style={{
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: tooltipPos.transform,
            }}
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-yhelp-black/70 dark:text-yhelp-yellow">
              {tour.title}
            </p>
            <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
              <div
                className="h-full rounded-full bg-yhelp-yellow transition-all duration-slow motion-reduce:transition-none"
                style={{ width: `${((index + 1) / steps.length) * 100}%` }}
                role="progressbar"
                aria-valuenow={index + 1}
                aria-valuemin={1}
                aria-valuemax={steps.length}
                aria-label="Progresso do tour"
              />
            </div>
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-yhelp-yellow text-sm font-bold text-yhelp-black">
                {index + 1}
              </span>
              <p className="text-xs font-medium text-neutral-500">
                Passo {index + 1} de {steps.length}
              </p>
            </div>
            <h2 id="tour-title" className="text-lg font-bold text-yhelp-black dark:text-white">
              {step.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
              {skipStep
                ? 'Você ainda não tem chamados. Crie um com Novo chamado e depois refaça este tour pelo botão de ajuda.'
                : step.body}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => void finishTour(true)}>
                Fechar tour
              </Button>
              {index > 0 ? (
                <Button variant="secondary" onClick={() => setIndex((i) => i - 1)}>
                  Anterior
                </Button>
              ) : null}
              {index < steps.length - 1 ? (
                <Button
                  onClick={() => {
                    if (skipStep) {
                      void finishTour(false);
                      return;
                    }
                    setIndex((i) => i + 1);
                  }}
                >
                  {skipStep ? 'Concluir' : 'Próximo'}
                </Button>
              ) : (
                <Button onClick={() => void finishTour(false)}>Concluir</Button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </TourContext.Provider>
  );
}

export function useProductTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useProductTour must be used within ProductTourProvider');
  return ctx;
}

/** @deprecated Use useProductTour().startTour('welcome') */
export function useRestartTour(): (tourId?: string) => void {
  const { startTour } = useProductTour();
  return (tourId = 'welcome') => startTour(tourId);
}
